import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HousingDataService, SortService, CampusWard, CampusWardsDataService, ConfirmDialog,
         SelectableCampusWardSessionInformation, SelectableApprovedHousingSessionInformation, MessageDialog, ApprovedAddress, ApprovedHousingComplex } from '../../../shared';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  templateUrl: './housing-ward-assignments.component.html',
  styleUrls: ['./housing-ward-assignments.component.scss']
})
export class HousingWardAssignmentsComponent implements OnInit, OnDestroy {

  loading = false;
  loadingError = false;

  housingWards: SelectableCampusWardSessionInformation[];
  selectedHousingWardIndex?: number = null;

  wardComplexes: SelectableApprovedHousingSessionInformation[];
  selectedWardComplexIndex?: number = null;

  assignableCampusWards: CampusWard[];
  selectedWardToAssign: CampusWard;
  reassigningWard = false;
  reassignedWard: CampusWard; // Lets us know that a reassignment happened so that we can show the "success" message

  removingWardAssignment = false;
  complexOfRemovedWardAssignment: ApprovedHousingComplex = null;

  sessionNumber: number;

  private campusWardId = NaN;
  private complexId = NaN;
  private parameterSubscription: Subscription;
  private queryParameterSubscription: Subscription;

  constructor(private route: ActivatedRoute, private dialog: MatDialog,
              private housingService: HousingDataService, private wardService: CampusWardsDataService,
              private sorter: SortService) {
      // subscribe to our route parameters' values for the ward or complex and the semester
      this.parameterSubscription = this.route.params.subscribe(params => {
        this.sessionNumber = +params['sessionNumber'];
        this.loadInformation();
      });

      this.queryParameterSubscription = this.route.queryParams.subscribe(params => {
        this.campusWardId = +params['campusWardId'];
        this.complexId =  +params['complexId'];
        this.loadInformation();
      });
    }

  ngOnInit() {
    // load the wards the user can pick from
    this.wardService.getCampusWards().subscribe(wards => {
          this.assignableCampusWards = wards.filter(ward => !ward.isForMarriedStudents);
        });
  }

  ngOnDestroy() {
    this.parameterSubscription.unsubscribe();
    this.queryParameterSubscription.unsubscribe();
  }

  /**
   * Confirms with the user that they want to reassign the selected wards and then does it.
   */
  reassignWard() {
    const addresses = this.getApprovedAddressesToUpdate();
    if (addresses.length === 0 || !this.selectedWardToAssign || this.reassigningWard) {
      return;
    }

    const pluralizedPart = addresses.length === 1 ? 'this approved housing address' : `these ${addresses.length} approved housing addresses`;
    const message = `Are you sure you want to assign ${pluralizedPart} to ${this.selectedWardToAssign.name}?` +
                    ' This assignment will be effective for the selected semester and ALL semesters thereafter.';
    const confirmDialog = this.openConfirmDialog(message);

    confirmDialog.afterClosed().subscribe(response => {
      if (response) {
        this.clearResultsOfReassignmentOrRemoval();
        this.reassigningWard = true;

        // store this off before in case the user changes their selection while we're updating the addresses
        const wardToAssign = this.selectedWardToAssign;

        this.housingService.updateWardAssignment(wardToAssign.campusWardId, this.sessionNumber, addresses.map(a => a.fullAddress))
          .pipe(
            finalize(() => this.reassigningWard = false)
          )
          .subscribe(() => {
            this.reassignedWard = wardToAssign;
            this.reloadAfterReassignmentOrRemoval();
          },
          error => {
            this.showError('Unable to reassign the ward of the selected addresses.');
          });
      }
    });
  }

  /**
   * Confirms with the user that they want to remove the assigned ward of the selected addresses, and if they confirm,
   * does it.
   */
  removeWardAssignment() {
    const addresses = this.getApprovedAddressesToUpdate();
    if (addresses.length === 0 || this.removingWardAssignment) {
      return;
    }

    const pluralizedPart = addresses.length === 1 ? 'this approved housing address' : `these ${addresses.length} approved housing addresses`;
    const message = `Are you sure you want to remove the assigned ward of ${pluralizedPart}?` +
                    ' This will be effective for the selected semester and ALL semesters thereafter.';
    const confirmDialog = this.openConfirmDialog(message);

    confirmDialog.afterClosed().subscribe(response => {
      if (response) {
        this.clearResultsOfReassignmentOrRemoval();
        this.removingWardAssignment = true;

        this.housingService.removeWardAssignment(this.sessionNumber, addresses.map(a => a.fullAddress))
          .pipe(
            finalize(() => this.removingWardAssignment = false)
          )
          .subscribe(() => {
            this.complexOfRemovedWardAssignment = addresses[0].complex;
            this.reloadAfterReassignmentOrRemoval();
          },
          error => {
            this.showError('Unable to reassign the ward of the selected addresses.');
          });
      }
    });
  }

  /**
   * After we reassign addresses or remove their ward assignment, we need to reload our data to reflect.  This does that as well as
   * tries to preserve the currently selected tab if applicable.
   */
  private reloadAfterReassignmentOrRemoval() {
    let selectedComplexName: string = null;
    let selectedWardId: number = null;

    if (this.selectedHousingWardIndex != null && this.selectedHousingWardIndex >= 0) {
      selectedWardId = this.housingWards[this.selectedHousingWardIndex].campusWardSessionInformation.campusWard.campusWardId;
    } else if (this.selectedWardComplexIndex != null && this.selectedWardComplexIndex >= 0) {
      selectedComplexName = this.wardComplexes[this.selectedWardComplexIndex].approvedHousingSessionInformation.housingComplex;
    }

    // reload our data since it's changed; we'll preserve their selection; base how we reload on what they are searching by
    if (this.campusWardId) {
      this.loadAddressesAssignedToWard(selectedComplexName);
    } else {
      this.loadAddressesAssignedToComplex(selectedWardId);
    }
  }

  /**
   * Clears the results of removing a ward assignment or reassigning one.
   */
  private clearResultsOfReassignmentOrRemoval() {
    this.reassignedWard = null;
    this.complexOfRemovedWardAssignment = null;
  }

  /**
   * Shows an error dialog.
   */
  private showError(message: string) {
    const errorDialog = this.dialog.open(MessageDialog, {
      data: { message: message, isError: true }
    });
  }

  /**
   * Gets the addresses that should be updated for what the user wants to do (clear or reassign).  Put it in a method
   * because we can be working with addresses grouped by complex or by ward, and this handles it.
   */
  private getApprovedAddressesToUpdate(): ApprovedAddress[] {
    if (this.selectedHousingWardIndex != null && this.selectedHousingWardIndex >= 0) {
      return this.housingWards[this.selectedHousingWardIndex].selectedAddresses;
    } else if (this.selectedWardComplexIndex != null && this.selectedWardComplexIndex >= 0) {
      return this.wardComplexes[this.selectedWardComplexIndex].selectedAddresses;
    }

    return [];
  }

  /**
   * Opens a confirm dialog with the given message.
   */
  private openConfirmDialog(message: string): MatDialogRef<ConfirmDialog, any> {
    return this.dialog.open(ConfirmDialog, {
      autoFocus: false,
      data: { title: 'Please confirm', message: message}
    });
  }

  /**
   * Based on our parameters, load the addresses assigned to the ward or complex for the given semester.
   */
  private loadInformation() {
    this.wardComplexes = null;
    this.selectedWardComplexIndex = null;
    this.housingWards = null;
    this.selectedHousingWardIndex = null;
    this.clearResultsOfReassignmentOrRemoval();

    if (!isNaN(this.sessionNumber) && (!isNaN(this.campusWardId) || !isNaN(this.complexId))) {
      // if they give us both, only look take into account the housing complex
      if (this.campusWardId) {
        this.loadAddressesAssignedToWard();
      } else {
        this.loadAddressesAssignedToComplex();
      }
    }
  }

  /**
   * Loads all of the addresses that have been assigned to the ward for the given semester.  This information comes
   * grouped by housing complex.
   */
  private loadAddressesAssignedToWard(complexNameToSelect?: string) {
    if (!this.loading && !isNaN(this.sessionNumber) && !isNaN(this.campusWardId)) {
      this.loading = true;
      this.housingService.getHousingComplexSessionInformation(this.campusWardId, this.sessionNumber)
      .pipe(
        finalize(() => this.loading = false)
      )
      .subscribe(information => {
          // we need to be able to select addresses, so use a wrapper to make this easier
          const wardComplexes = information;
          wardComplexes.sort(this.sorter.sortApprovedHousingSessionInformationAsc);

          this.wardComplexes = wardComplexes.map((wardComplex) => {
            return new SelectableApprovedHousingSessionInformation(wardComplex);
          });

          // if what was selected before the loading is still in our results, keep it selected, otherwise select the first one
          let indexToSelect = 0; // assume we'll just use the first one
          if (complexNameToSelect) {
            const index = this.wardComplexes.map(w => w.approvedHousingSessionInformation.housingComplex)
                                            .indexOf(complexNameToSelect);
            if (index > -1) {
              indexToSelect = index;
            }
          }
          if (this.wardComplexes.length > 0) {
            this.selectedWardComplexIndex = indexToSelect;
          }
        },
        error => {
          this.loadingError = true;
        });
    }
  }

  /**
   * Loads all of the addresses that have been assigned to the housing complex for the given semester.
   * This information comes grouped by ward.
   */
  private loadAddressesAssignedToComplex(wardIdToSelect?: number) {
    if (!this.loading && !isNaN(this.sessionNumber) && !isNaN(this.complexId)) {
      this.loading = true;
      this.housingService.getCampusWardSessionInformation(this.complexId, this.sessionNumber)
      .pipe(
        finalize(() => this.loading = false)
      )
      .subscribe(information => {
          // we need to be able to select addresses, so use a wrapper to make this easier
          const housingWards = information;
          housingWards.sort(this.sorter.sortCampusWardSessionInformationsAsc);

          this.housingWards = housingWards.map((housingWard) => {
            return new SelectableCampusWardSessionInformation(housingWard);
          });

          // if what was selected before the loading is still in our results, keep it selected, otherwise select the first one
          let indexToSelect = 0; // assume we'll just use the first one
          if (wardIdToSelect) {
            const index = this.housingWards.map(w => w.campusWardSessionInformation.campusWard.campusWardId)
                                           .indexOf(wardIdToSelect);
            if (index > -1) {
              indexToSelect = index;
            }
          }
          if (this.housingWards.length > 0) {
            this.selectedHousingWardIndex = indexToSelect;
          }
        },
        error => {
          this.loadingError = true;
        });
    }
  }
}
